import { useEffect, useState } from 'react';
import { useChannelStateContext, useTypingContext } from 'stream-chat-react';
import { UserResponse } from 'stream-chat';
import { useAtom, useSetAtom } from 'jotai';
import { useErrorHandler } from 'react-error-boundary';

import { useChatClient } from '@bizzabo/chat-core';
import { UserBadge } from '@bizzabo/collaboration-common';
import { ArrowLeftSLine, Text, FilledVideoCamera, IconButton, Theme } from '@bizzabo/design-system';
import { useCurrentUser, User, useUserByProfileId } from '@bizzabo/entities';
import { makeStyles } from '@material-ui/styles';

import { useMessages, View } from '../../../contexts/MessagesContext';
import useTypeTracking from '../../../../../analytics';
import MeetButton from '../MeetButton';
import { createVideoAtom } from '../../../../../video';
import { incomingCallAtom } from '../../../../../notifications/videoNotifications/atoms';
import ViewInfo from '../ViewInfo';
import useVideoEnable from '../../../../../hooks/useVideoEnable';
import { SelectedAppToolAtom } from '../../../../../tools/atoms/appToolsAtom';
import { BuildInAppToolId } from '../../../../../tools/types/appTools';
import {
    MeetingsToolsParamsAtom,
    MeetingsToolsParamsType,
} from '../../../../../appState/meetings/atoms';

interface ChannelHeaderProps {
    channelStatus: number;
    guestUser: UserResponse<User>;
    guestProfileId: number;
}

const ChannelHeader: React.FC<ChannelHeaderProps> = ({
    channelStatus,
    guestUser,
    guestProfileId,
}) => {
    const classes = useStyles();
    const { eventId, setView, eventUniqueName } = useMessages();
    const setSelectedApp = useSetAtom(SelectedAppToolAtom);
    const setMeetingsToolsParams = useSetAtom(MeetingsToolsParamsAtom);
    const { data: shouldDisplayVideo } = useVideoEnable(eventUniqueName);
    const { trackClickEvent } = useTypeTracking();
    const { client } = useChatClient();
    const { channel } = useChannelStateContext();
    const [unreadChannelsCount, setUnreadChannelsCount] = useState(0);
    const createVideo = useSetAtom(createVideoAtom);
    const [incomingCall] = useAtom(incomingCallAtom);
    const { typing = {} } = useTypingContext();
    const { data: currentUser } = useCurrentUser(eventId);
    const [isViewInfoOpen, setIsViewInfoOpen] = useState(false);
    const handleError = useErrorHandler();
    const { data: userByProfileId, isLoading: isLoadingUser } = useUserByProfileId({
        eventId,
        userProfileId: guestProfileId,
        options: { staleTime: Infinity },
    });

    useEffect(() => {
        const clientListener = client.on(event => {
            const isPrivateChannel = event.channel_type === 'private';
            const isNewMessageEvent =
                event.type === 'notification.message_new' || event.type === 'message.new';
            const isCurrentChannel = event.channel_id === channel.id;
            const isMessageRead = event.type === 'notification.mark_read';

            if (isPrivateChannel && (isNewMessageEvent || isMessageRead) && !isCurrentChannel) {
                setUnreadChannelsCount(event.unread_channels);
            }
        });

        return () => {
            clientListener.unsubscribe();
        };
    }, [client, channel]);

    const handleBackClick = () => {
        setView(View.ChannelList);
        trackClickEvent({
            subCategory: 'BACK_TO_CHANNEL_LIST',
        });
    };

    const handleMeetButtonClick = async () => {
        try {
            await createVideo({
                callerUserProfileId: currentUser.userProfileId,
                eventId,
                members: [guestProfileId],
                source: { type: 'CHAT', channelId: channel.id },
            });
        } catch (error) {
            handleError(error);
        }

        trackClickEvent({
            subCategory: 'MEETING_INVITATION_TYPE_CHOSEN',
            description: {
                flow: 'CREATE_MEETING',
                meeting_type: 'MEET_NOW',
            },
        });
    };

    const handleScheduleClick = () => {
        trackClickEvent({
            subCategory: 'MEETING_INVITATION_TYPE_CHOSEN',
            description: {
                flow: 'CREATE_MEETING',
                meeting_type: 'SCHEDULED',
            },
        });
        setSelectedApp(BuildInAppToolId.Meetings);
        setMeetingsToolsParams({
            type: MeetingsToolsParamsType.OpenWizard,
            otherAttendeeId: guestProfileId,
        });
    };

    const isTyping = Boolean(
        Object.values(typing).filter(
            ({ parent_id, user }) => user?.id !== client.user?.id && !parent_id
        )[0]
    );

    const toogleViewInfoHandle = () => {
        setIsViewInfoOpen(isViewInfoOpen => !isViewInfoOpen);
    };

    return (
        <div className={classes.header}>
            <div className={classes.topBar}>
                <div className={classes.leftSide} onClick={handleBackClick}>
                    <IconButton icon={ArrowLeftSLine} className={classes.iconButton} />
                    <Text variant="t80" className={classes.title}>
                        All Messages
                    </Text>
                    {unreadChannelsCount > 0 && (
                        <Text variant="t81" className={classes.unreadBadge}>
                            {unreadChannelsCount}
                        </Text>
                    )}
                </div>
            </div>

            <div className={classes.userHeader}>
                <div className={classes.userBadgeWrapper}>
                    <UserBadge
                        user={guestUser}
                        sizeOfAvatar={40}
                        titleVariant="t60"
                        badge={incomingCall.hasIncomingCall && <FilledVideoCamera />}
                        isTyping={isTyping}
                        className={classes.userBadge}
                    />
                    {shouldDisplayVideo && (
                        <MeetButton
                            channelStatus={channelStatus}
                            guestUser={guestUser}
                            onMeetNowClick={handleMeetButtonClick}
                            onScheduleClick={handleScheduleClick}
                        />
                    )}
                </div>
                {!isLoadingUser && (
                    <ViewInfo
                        userByProfileId={userByProfileId}
                        isViewInfoOpen={isViewInfoOpen}
                        toogleViewInfoHandle={toogleViewInfoHandle}
                    />
                )}
            </div>
        </div>
    );
};

const useStyles = makeStyles<Theme>(
    theme => ({
        header: {
            display: 'flex',
            flexDirection: 'column',
            borderBottom: `solid 1px ${theme.palette.greys[300]}`,
        },
        topBar: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            height: 48,
            padding: '42px 24px 22px',
            borderRadius: 0,
            backgroundColor: theme.palette.greys[100],
            [theme.breakpoints.up('sm')]: {
                borderRadius: '33px 33px 0 0',
            },
            [theme.breakpoints.down('sm')]: {
                padding: '25px 24px',
            },
        },
        leftSide: {
            display: 'flex',
        },
        title: {
            color: '#939393',
            margin: '3px 6px',
            cursor: 'pointer',
        },
        iconButton: {
            width: 24,
            height: 24,
            background: 'none',
            '&:hover': {
                background: 'none',
            },
        },
        userBadgeWrapper: {
            display: 'flex',
            flexDirection: 'row',
            width: '100%',
            marginRight: 8,
        },
        unreadBadge: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 20,
            height: 20,
            background: theme.palette.notification,
            color: theme.palette.greys[100],
            borderRadius: 30,
            fontSize: '1rem',
            fontWeight: 600,
            marginTop: 1,
        },
        userHeader: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '0 24px 0',
            backgroundColor: theme.palette.greys[100],
            transition: 'all 0.2s linear',
        },
        userBadge: {
            overflow: 'auto',
        },
        cameraNotification: {},
    }),
    { classNamePrefix: ChannelHeader.name }
);

export default ChannelHeader;
